/**
 * Performance-optimized logger with production mode support
 * 
 * In development: Logs are deferred using InteractionManager so they don't block touch events
 * In production mode: ALL logging is completely disabled (no-ops)
 * In release builds (non-production mode): Only errors and warnings are logged
 * 
 * Usage:
 *   import { logger } from '../utils/logger';
 *   logger.info('ProductDetails', 'Loading product', { id: productId });
 *   logger.error('Cart', 'Failed to add item', error);
 * 
 * Production Mode:
 *   Set EXPO_PUBLIC_PRODUCTION_MODE=true in your .env file to disable ALL logging
 */

import { InteractionManager, Platform } from 'react-native';
import { APP_CONFIG, isLoggingEnabled, isStrictProductionMode } from '../config/appConfig';

// Log levels
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Queue for batching logs
let logQueue: Array<{ level: LogLevel; args: any[] }> = [];
let isProcessing = false;
let flushTimeout: NodeJS.Timeout | null = null;

// Check if logging is completely disabled (strict production mode)
const shouldLog = (): boolean => !isStrictProductionMode();

// Check if dev-only logs should be shown
const shouldLogDev = (): boolean => isLoggingEnabled();

// Process the log queue after interactions complete
const processQueue = () => {
  if (isProcessing || logQueue.length === 0 || !shouldLog()) return;
  
  isProcessing = true;
  
  // Use InteractionManager to wait for animations/touches to complete
  InteractionManager.runAfterInteractions(() => {
    // Process all queued logs
    const queueCopy = [...logQueue];
    logQueue = [];
    isProcessing = false;
    
    queueCopy.forEach(({ level, args }) => {
      switch (level) {
        case 'debug':
          console.log(...args);
          break;
        case 'info':
          console.log(...args);
          break;
        case 'warn':
          console.warn(...args);
          break;
        case 'error':
          console.error(...args);
          break;
      }
    });
  });
};

// Debounced flush - waits for a batch of logs before processing
const scheduleFlush = () => {
  if (flushTimeout || !shouldLog()) return;
  
  flushTimeout = setTimeout(() => {
    flushTimeout = null;
    processQueue();
  }, 16); // ~1 frame delay to batch multiple logs
};

// Queue a log entry
const queueLog = (level: LogLevel, ...args: any[]) => {
  if (!shouldLog()) return;
  logQueue.push({ level, args });
  scheduleFlush();
};

// Format tag for consistent log output
const formatTag = (tag: string) => `[${tag}]`;

/**
 * Logger with deferred, non-blocking output
 * Respects PRODUCTION_MODE flag - all logging disabled when production = true
 */
export const logger = {
  /**
   * Debug-level logging (development only, fully deferred)
   * Use for verbose/frequent logs that are only needed during debugging
   * Disabled in: production mode, release builds
   */
  debug: (tag: string, message: string, data?: any) => {
    if (!shouldLogDev()) return; // No-op in production or release
    
    const args = data !== undefined 
      ? [`🔍 ${formatTag(tag)} ${message}`, data]
      : [`🔍 ${formatTag(tag)} ${message}`];
    
    queueLog('debug', ...args);
  },

  /**
   * Info-level logging (development only, deferred)
   * Use for general informational messages
   * Disabled in: production mode, release builds
   */
  info: (tag: string, message: string, data?: any) => {
    if (!shouldLogDev()) return; // No-op in production or release
    
    const args = data !== undefined 
      ? [`ℹ️ ${formatTag(tag)} ${message}`, data]
      : [`ℹ️ ${formatTag(tag)} ${message}`];
    
    queueLog('info', ...args);
  },

  /**
   * Warning-level logging (normally always enabled, but respects production mode)
   * Use for potential issues that don't break functionality
   * Disabled in: production mode only
   */
  warn: (tag: string, message: string, data?: any) => {
    if (!shouldLog()) return; // No-op in strict production mode
    
    const args = data !== undefined 
      ? [`⚠️ ${formatTag(tag)} ${message}`, data]
      : [`⚠️ ${formatTag(tag)} ${message}`];
    
    if (shouldLogDev()) {
      queueLog('warn', ...args);
    } else {
      // In release builds (non-production mode), warnings go immediately
      console.warn(...args);
    }
  },

  /**
   * Error-level logging (normally always enabled, but respects production mode)
   * Use for actual errors that need attention
   * Disabled in: production mode only
   * Note: Sentry still captures errors even when logging is disabled
   */
  error: (tag: string, message: string, error?: any) => {
    if (!shouldLog()) return; // No-op in strict production mode
    
    const args = error !== undefined 
      ? [`❌ ${formatTag(tag)} ${message}`, error]
      : [`❌ ${formatTag(tag)} ${message}`];
    
    // Errors always log immediately - they're critical
    console.error(...args);
  },

  /**
   * Immediate log (bypasses queue) - use sparingly
   * Only for critical debugging that MUST be synchronous
   * Disabled in: production mode, release builds
   */
  immediate: (tag: string, message: string, data?: any) => {
    if (!shouldLogDev()) return;
    
    if (data !== undefined) {
      console.log(`⚡ ${formatTag(tag)} ${message}`, data);
    } else {
      console.log(`⚡ ${formatTag(tag)} ${message}`);
    }
  },

  /**
   * Start a performance timer (development only)
   * Disabled in: production mode, release builds
   */
  time: (label: string) => {
    if (!shouldLogDev()) return;
    console.time(label);
  },

  /**
   * End a performance timer (development only)
   * Disabled in: production mode, release builds
   */
  timeEnd: (label: string) => {
    if (!shouldLogDev()) return;
    console.timeEnd(label);
  },

  /**
   * Flush all pending logs immediately
   * Call this before app backgrounding or crash reporting
   */
  flush: () => {
    if (!shouldLog()) return;
    
    if (flushTimeout) {
      clearTimeout(flushTimeout);
      flushTimeout = null;
    }
    
    const queueCopy = [...logQueue];
    logQueue = [];
    isProcessing = false;
    
    queueCopy.forEach(({ level, args }) => {
      switch (level) {
        case 'debug':
        case 'info':
          console.log(...args);
          break;
        case 'warn':
          console.warn(...args);
          break;
        case 'error':
          console.error(...args);
          break;
      }
    });
  },

  /**
   * Check if production mode is enabled
   */
  isProductionMode: () => isStrictProductionMode(),

  /**
   * Check if logging is enabled
   */
  isLoggingEnabled: () => shouldLog(),
};

// Export a quick console replacement for gradual migration
// Can use: log.info('message') instead of console.log('message')
export const log = {
  debug: (...args: any[]) => {
    if (!shouldLogDev()) return;
    queueLog('debug', ...args);
  },
  info: (...args: any[]) => {
    if (!shouldLogDev()) return;
    queueLog('info', ...args);
  },
  warn: (...args: any[]) => {
    if (!shouldLog()) return;
    queueLog('warn', ...args);
  },
  error: (...args: any[]) => {
    if (!shouldLog()) return;
    console.error(...args); // Errors always immediate
  },
};

export default logger;
